import React, { useCallback, useEffect } from 'react'
import loadable from '@loadable/component'

import { useIntl } from 'intl'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { constants } from 'helpers'
import { track } from 'analytics'
import { useAb } from 'hooks'
import { Link } from 'router'
import links from 'links'
import { useUser } from 'modules/user'

import { useSubscription } from 'modules/subscription'
import { Avatar } from 'components/dataDisplay'
import { HoverablePopover } from 'components/navigation'
import { Bone } from 'components/layout'
import { navMessages } from 'compositions/navigation'
import { useUserNav } from '../../util'

import Section from './components/Section/Section'


const QueueBanner = loadable(
  () => import('compositions/Header/components/QueueBanner/QueueBanner'),
  { fallback: <Bone className="gray-90 !mix-blend-normal" pw={100} h={100} /> }
)

const UserNav: React.FunctionComponent = () => {
  const sections = useUserNav()

  const { user } = useUser()
  const firstName = user?.firstName
  const lastName = user?.lastName

  const intl = useIntl()
  const buttonAriaLabel = intl.formatMessage(navMessages.aria.profileButton)

  const { subscription } = useSubscription()
  const isSubscribed = subscription?.isSubscribed

  const isQueueNavBannerEnabled = useAb(constants.abTests.queueNavBanner) === 'b'
  const isQueueNavBannerVisible = isQueueNavBannerEnabled && isSubscribed

  // TODO: Remove this tracking after CRO-344 ab test is over
  const handleDisplay = useCallback(() => {
    track('Desktop profile menu display')
  }, [])

  return (
    <HoverablePopover className="align-center relative flex h-full justify-center">
      {
        ({ open, close }) => (
          <>
            <HoverablePopover.Button data-testid="profileMenuBtn" aria-label={buttonAriaLabel}>
              <Avatar
                firstName={firstName}
                lastName={lastName}
                size={40}
                isActive={open}
                data-testid="userAvatar"
              />
            </HoverablePopover.Button>
            <HoverablePopover.Panel as="nav" className="absolute right-0 bg-black pr-40 pt-32 text-right desktop:top-full desktop:w-[368rem]" aria-label="Profile">
              <DisplayTracker onDisplay={handleDisplay} />
              {
                isQueueNavBannerVisible && (
                  <Link
                    className="-mr-24 -mt-16 mb-40 ml-16 block"
                    to={links.queue}
                    onClick={() => {
                      track('Desktop profile menu click', {
                        link: links.queue,
                        title: 'Manage your fragrances',
                        blockName: 'Your membership',
                      })
                      close()
                    }}
                  >
                    <QueueBanner isDark />
                  </Link>
                )
              }
              {
                sections.map((data) => (
                  <Section key={data.id} data={data} closeNav={close} />
                ))
              }
            </HoverablePopover.Panel>
          </>
        )
      }
    </HoverablePopover>
  )
}

const DisplayTracker: React.FC<{ onDisplay: () => void }> = ({ onDisplay }) => {
  const isAaTestUserNav = useFeatureIsOn(constants.features.aaUserNav)

  useEffect(() => {
    onDisplay()
  }, [ onDisplay ])

  return null
}


export default React.memo(UserNav)
